import {
  defaultsFor,
  pendulum,
  collision,
  collisionOutcome,
  incline,
  circular,
  normalizeParams,
  TAU,
} from '@physlab/core';

function now() {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function measureCheck(name, fn, tolerance, unit = '') {
  const start = now();
  try {
    const result = fn();
    return {
      name,
      passed: result.value <= tolerance,
      metric: result.value,
      tolerance,
      unit,
      detail: result.detail,
      durationMs: now() - start,
    };
  } catch (error) {
    return {
      name,
      passed: false,
      metric: Number.POSITIVE_INFINITY,
      tolerance,
      unit,
      detail: error instanceof Error ? error.message : String(error),
      durationMs: now() - start,
    };
  }
}

/** 浏览器内可运行的五项守恒/护栏自检，与 node 单元测试相互独立。 */
export function runConservationSelfTest() {
  return [
    measureCheck(
      '单摆机械能守恒',
      () => {
        const params = { ...defaultsFor(pendulum), damping: 0, initialAngle: 30 };
        let state = pendulum.createState(params);
        const baseline = pendulum.reference(state, params);
        for (let i = 0; i < 3840; i += 1) state = pendulum.advance(state, params, 1 / 480);
        const total = pendulum.measure(state, params).energy.total;
        const drift = Math.abs(total - baseline.energy) / baseline.energy;
        return { value: drift, detail: `8 s 仿真后相对漂移 ${(drift * 100).toExponential(3)}%` };
      },
      1e-5,
      '相对误差',
    ),
    measureCheck(
      '弹性碰撞动量/动能',
      () => {
        const result = collisionOutcome(2, 1, 3, -1, 1);
        const pBefore = 2 * 3 + 1 * -1;
        const pAfter = 2 * result.v1 + 1 * result.v2;
        const eBefore = 0.5 * 2 * 9 + 0.5 * 1 * 1;
        const eAfter = 0.5 * 2 * result.v1 ** 2 + 0.5 * 1 * result.v2 ** 2;
        const pError = Math.abs(pAfter - pBefore) / Math.max(1, Math.abs(pBefore));
        const eError = Math.abs(eAfter - eBefore) / eBefore;
        const value = Math.max(pError, eError);
        return { value, detail: `动量误差 ${pError.toExponential(3)}，动能误差 ${eError.toExponential(3)}` };
      },
      1e-10,
      '相对误差',
    ),
    measureCheck(
      '光滑斜面能量守恒',
      () => {
        const params = { ...defaultsFor(incline), friction: 0, angle: 30, length: 5, initialSpeed: 0 };
        let state = incline.createState(params);
        const baseline = incline.reference(state, params);
        for (let i = 0; i < 4000 && state.status !== 'bottom'; i += 1) {
          state = incline.advance(state, params, 1 / 240);
        }
        const total = incline.measure(state, params).energy.total;
        const drift = Math.abs(total - baseline.energy) / baseline.energy;
        return { value: drift, detail: `到达底端后机械能相对偏差 ${(drift * 100).toExponential(3)}%` };
      },
      1e-6,
      '相对误差',
    ),
    measureCheck(
      '圆周运动机械能守恒',
      () => {
        const params = { ...defaultsFor(circular), damping: 0 };
        let state = circular.createState(params);
        const baseline = circular.reference(state, params);
        for (let i = 0; i < 2880; i += 1) state = circular.advance(state, params, 1 / 480);
        const total = circular.measure(state, params).energy.total;
        const drift = Math.abs(total - baseline.energy) / baseline.energy;
        return { value: drift, detail: `6 s 仿真后相对漂移 ${(drift * 100).toExponential(3)}%` };
      },
      1e-5,
      '相对误差',
    ),
    measureCheck(
      '参数安全护栏',
      () => {
        const normalized = normalizeParams(pendulum, {
          ...defaultsFor(pendulum),
          length: 999,
          initialAngle: Number.NaN,
        });
        const failures =
          (normalized.params.length === 3 ? 0 : 1) +
          (normalized.params.initialAngle === 30 ? 0 : 1) +
          (normalized.repairs.length >= 2 ? 0 : 1);
        return { value: failures, detail: `越界与 NaN 参数修复记录 ${normalized.repairs.length} 条` };
      },
      0,
      '失败项',
    ),
  ];
}

export function selfTestSummary(results) {
  const passed = results.filter((item) => item.passed).length;
  return {
    passed,
    total: results.length,
    ok: passed === results.length,
    label: `${passed}/${results.length}`,
  };
}
